import { Controller } from "@hotwired/stimulus"
import Tabs from "flowbite/lib/esm/components/tabs"

// Flowbite tabs. Each [role="tab"] names its panel with aria-controls, as the
// accordion does:
//
//   <ul data-controller="tabs" role="tablist">
//     <li><button role="tab" id="a-tab" aria-controls="a" aria-selected="true">A</button></li>
//   </ul>
//   <div id="a" role="tabpanel">…</div>

// Add data-tabs-persist-value="true" to remember the chosen tab in
// sessionStorage (keyed by the list's id). Off by default: several pages reuse
// the same list id, and a choice made on one page must not leak into another.
export default class extends Controller {
  static values = { persist: Boolean }

  connect() {
    const items = Array.from(this.element.querySelectorAll('[role="tab"]'))
      .map((triggerEl) => ({
        id: triggerEl.id,
        triggerEl,
        targetEl: document.getElementById(
          triggerEl.getAttribute("aria-controls"),
        ),
      }))
      .filter((item) => {
        if (item.targetEl) return true
        // Flowbite's Tabs dereferences every panel on show(); a tab whose
        // panel isn't rendered yet would take the whole tab list down.
        console.warn(
          `tabs: no panel "${item.triggerEl.getAttribute("aria-controls")}" for tab "${item.id}", skipping`,
        )
        return false
      })

    const storedId = this.persistValue
      ? sessionStorage.getItem(this.storageKey)
      : null
    const defaultTabId = items.some((item) => item.id === storedId)
      ? storedId
      : items.find(
          (item) => item.triggerEl.getAttribute("aria-selected") === "true",
        )?.id

    // Only pass the class options when the list declares them; an undefined
    // value would override Flowbite's defaults and crash its show().
    const {
      tabsActiveClasses: activeClasses,
      tabsInactiveClasses: inactiveClasses,
    } = this.element.dataset

    this.tabs = new Tabs(this.element, items, {
      defaultTabId,
      ...(activeClasses && { activeClasses }),
      ...(inactiveClasses && { inactiveClasses }),
      onShow: (_instance, tab) => {
        if (this.persistValue) sessionStorage.setItem(this.storageKey, tab.id)
      },
    })
  }

  disconnect() {
    if (this.tabs) {
      this.tabs.destroyAndRemoveInstance()
      this.tabs = null
    }
  }

  get storageKey() {
    return `tabs:${this.element.id}`
  }
}
